import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material';
import { Quote } from '../../../../../../../Typescript/backend/classes';
import { QUOTE_INSURANCE_POLICIES } from '../../../../../../../queries';
import { QuoteInsurancePoliciesQuery } from '../../../../../../../gql/graphql';
import { StyledAccordion } from './sharedComponents';
import { isNotNullish } from '../../../../../../../util';
import { ComparePolicy } from './ComparePolicy';

type PolicyType = NonNullable<
  NonNullable<QuoteInsurancePoliciesQuery['quoteInsurancePolicies']>[number]
>;

interface MatchedPolicy {
  description: string;
  comparisonPolicy: PolicyType | null;
  policy: PolicyType | null;
}

interface CoveragesAccordionProps {
  quote?: Quote;
  comparisonQuote: Quote | null;
  loading?: boolean;
}

export const CoveragesAccordion = ({
  quote,
  comparisonQuote,
  loading,
}: CoveragesAccordionProps) => {
  const { token } = useAuth();
  const [quotePolicies, setQuotePolicies] = useState<Array<PolicyType>>([]);
  const [comparisonPolicies, setComparisonPolicies] = useState<
    Array<PolicyType>
  >([]);

  // Fetch coverages for the quote
  const [
    getQuoteInsurancePolicies,
    { data: quoteData, loading: quoteCoveragesLoading },
  ] = useLazyQuery(QUOTE_INSURANCE_POLICIES);

  // Fetch coverages for the comparison quote
  const [
    getComparisonQuoteInsurancePolicies,
    { data: comparisonQuoteData, loading: comparisonQuoteCoveragesLoading },
  ] = useLazyQuery(QUOTE_INSURANCE_POLICIES);

  // Fetch coverages when quote changes
  useEffect(() => {
    if (token && quote?.uniqueId) {
      getQuoteInsurancePolicies({
        variables: {
          token,
          uniqueId: quote.uniqueId,
        },
      });
    }
  }, [token, quote?.uniqueId, getQuoteInsurancePolicies]);

  // Fetch coverages when comparisonQuote changes
  useEffect(() => {
    if (token && comparisonQuote?.uniqueId) {
      getComparisonQuoteInsurancePolicies({
        variables: {
          token,
          uniqueId: comparisonQuote.uniqueId,
        },
      });
    }
  }, [token, comparisonQuote?.uniqueId, getComparisonQuoteInsurancePolicies]);

  // Update quotePolicies from query data
  useEffect(() => {
    if (quoteData?.quoteInsurancePolicies) {
      const filtered = quoteData.quoteInsurancePolicies.filter(isNotNullish);
      setQuotePolicies(filtered);
    }
  }, [quoteData?.quoteInsurancePolicies]);

  // Update comparisonPolicies from query data, or clear when comparisonQuote is null
  useEffect(() => {
    if (!comparisonQuote) {
      setComparisonPolicies([]);
    } else if (comparisonQuoteData?.quoteInsurancePolicies) {
      const filtered =
        comparisonQuoteData.quoteInsurancePolicies.filter(isNotNullish);
      setComparisonPolicies(filtered);
    }
  }, [comparisonQuote, comparisonQuoteData?.quoteInsurancePolicies]);

  // Match policies by description
  const [matchedPolicies, setMatchedPolicies] = useState<Array<MatchedPolicy>>(
    [],
  );

  useEffect(() => {
    // Get all unique descriptions by combining arrays first, then filtering
    const allDescriptions = new Set<string>(
      [...comparisonPolicies, ...quotePolicies].map(
        (policy) => policy.description,
      ),
    );

    // Create matched pairs by mapping over descriptions and finding matching policies
    const matched = Array.from(allDescriptions).map((description) => ({
      description,
      comparisonPolicy:
        comparisonPolicies.find(
          (policy) => policy.description === description,
        ) || null,
      policy:
        quotePolicies.find((policy) => policy.description === description) ||
        null,
    }));

    setMatchedPolicies(matched);
  }, [comparisonPolicies, quotePolicies]);

  const isLoading =
    loading || comparisonQuoteCoveragesLoading || quoteCoveragesLoading;

  return (
    <StyledAccordion defaultExpanded elevation={0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant='h6'>Coverages</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isLoading ? (
          <CoveragesSkeleton />
        ) : (
          <CoveragesContainer>
            {matchedPolicies.length === 0 ? (
              <NoPoliciesFoundTypography variant='body2' color='text.secondary'>
                No policies found
              </NoPoliciesFoundTypography>
            ) : (
              matchedPolicies.map(
                ({ description, comparisonPolicy, policy }) => (
                  <MatchedPolicyContainer key={description}>
                    <ComparePolicy
                      policy={policy}
                      comparisonPolicy={comparisonPolicy}
                    />
                  </MatchedPolicyContainer>
                ),
              )
            )}
          </CoveragesContainer>
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
};

const CoveragesSkeleton = () => {
  return (
    <SkeletonContainer>
      {[...Array(4)].map((_, index) => (
        <SkeletonItem
          key={index}
          variant='rectangular'
          width='100%'
          height={40}
        />
      ))}
    </SkeletonContainer>
  );
};

const CoveragesContainer = styled(Box)({});

const NoPoliciesFoundTypography = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const MatchedPolicyContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SkeletonContainer = styled(Box)({});

const SkeletonItem = styled(Skeleton)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));
